/**
 * usePlanOverrides — draft state for the per-clinic plan-limit editor.
 *
 * Draft = the full restriction sheet (LIMIT_ROWS keys) for the *selected*
 * plan. It starts from the clinic's effective limits and can be edited per
 * row, reset per row / all, or re-seeded with a different plan's defaults.
 *
 * buildUpdates() turns the draft into clinic-doc field updates:
 *   - planOverrides: map with only keys that differ from the plan default
 *     (null → the caller / nivFirebase converts to deleteField())
 *   - legacy flat fields (video / AI report / storage): value, or null = default
 */

import { useCallback, useMemo, useState } from "react";
import {
  getPlanDefaults,
  getEffectiveLimits,
  OVERRIDE_KEYS,
  LEGACY_LIMIT_FIELDS,
  UNLIMITED,
} from "../../../../services/planLimits";
import { LIMIT_ROWS } from "./clinicDetailConstants";

const EDITABLE_KEYS = LIMIT_ROWS.filter((r) => r.type !== "readonly").map((r) => r.key);

const pickDraft = (limits) =>
  Object.fromEntries(EDITABLE_KEYS.map((k) => [k, limits?.[k] ?? null]));

/** Apply the same flag ↔ count coupling the enforcement layer applies */
const applyCoupling = (draft, changedKey) => {
  const next = { ...draft };
  if (changedKey === "canCreateRooms") {
    if (next.canCreateRooms === false) next.maxRooms = 0;
    else if (!(next.maxRooms > 0)) next.maxRooms = UNLIMITED;
  } else if (changedKey === "maxRooms") {
    next.canCreateRooms = Number(next.maxRooms) > 0;
  } else if (changedKey === "canAddTherapists") {
    if (next.canAddTherapists === false) next.maxTherapists = 1;
    else if (!(next.maxTherapists > 1)) next.maxTherapists = UNLIMITED;
  } else if (changedKey === "maxTherapists") {
    next.canAddTherapists = Number(next.maxTherapists) > 1;
  }
  return next;
};

/** Compare draft to plan defaults → diff rows ({key, effective, planDefault}) */
export const diffDraft = (planCode, draft) => {
  const defaults = getPlanDefaults(planCode);
  return EDITABLE_KEYS.filter((k) => draft[k] !== defaults[k]).map((k) => ({
    key: k,
    effective: draft[k],
    planDefault: defaults[k],
  }));
};

export default function usePlanOverrides(clinic) {
  const [planCode, setPlanCodeState] = useState(clinic?.plan || "trial");
  const [draft, setDraft] = useState(() => pickDraft(clinic?.effectiveLimits));

  const defaults = useMemo(() => getPlanDefaults(planCode), [planCode]);
  const diffs = useMemo(() => diffDraft(planCode, draft), [planCode, draft]);

  /** Re-seed from the saved clinic (cancel / after save) */
  const resetFromClinic = useCallback(
    (c = clinic) => {
      setPlanCodeState(c?.plan || "trial");
      setDraft(pickDraft(c?.effectiveLimits || getEffectiveLimits(c?.plan, c)));
    },
    [clinic]
  );

  /**
   * Change the selected plan. keepOverrides=false → draft becomes the new
   * plan's defaults; true → current values are kept (they will now read as
   * overrides against the new plan).
   */
  const setPlanCode = useCallback((nextPlan, { keepOverrides = false } = {}) => {
    setPlanCodeState(nextPlan);
    if (!keepOverrides) setDraft(pickDraft(getPlanDefaults(nextPlan)));
  }, []);

  const setValue = useCallback((key, value) => {
    setDraft((d) => applyCoupling({ ...d, [key]: value }, key));
  }, []);

  const resetField = useCallback(
    (key) => {
      setDraft((d) => applyCoupling({ ...d, [key]: defaults[key] }, key));
    },
    [defaults]
  );

  const resetAll = useCallback(() => setDraft(pickDraft(defaults)), [defaults]);

  /** Clinic-doc field updates derived from the draft (only fields that changed vs saved). */
  const buildUpdates = useCallback(() => {
    const updates = {};

    // planOverrides map: only keys ≠ plan default
    const overrides = {};
    OVERRIDE_KEYS.forEach((k) => {
      if (draft[k] !== defaults[k] && draft[k] != null) overrides[k] = draft[k];
    });
    const savedOverrides = clinic?.planOverrides || null;
    const nextOverrides = Object.keys(overrides).length > 0 ? overrides : null;
    if (JSON.stringify(savedOverrides || {}) !== JSON.stringify(nextOverrides || {})) {
      updates.planOverrides = nextOverrides; // null → deleteField() in nivFirebase
    }

    // Legacy flat fields: number → value or null (= plan default); boolean → explicit
    Object.entries(LEGACY_LIMIT_FIELDS).forEach(([key, def]) => {
      const v = draft[key];
      const savedRaw = clinic?.[def.field];
      if (def.type === "boolean") {
        const next = v !== false; // undefined/null → true (server checks `=== false`)
        const saved = savedRaw !== false;
        if (next !== saved) updates[def.field] = next;
        return;
      }
      const next = v == null || Number(v) === defaults[key] || !(Number(v) > 0) ? null : Number(v);
      const saved = typeof savedRaw === "number" && savedRaw > 0 && savedRaw !== defaults[key] ? savedRaw : null;
      if (next !== saved) updates[def.field] = next;
    });

    return updates;
  }, [draft, defaults, clinic]);

  return {
    planCode,
    setPlanCode,
    defaults,
    draft,
    diffs,
    setValue,
    resetField,
    resetAll,
    resetFromClinic,
    buildUpdates,
  };
}
